
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Settings, Users, MapPin, CreditCard, Bell, Shield, Database } from 'lucide-react';

const StaffSettings = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-juv-cream to-juv-sage/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-3xl font-bold font-juv-heading text-juv-deep-chestnut">
              Staff Settings
            </h1>
            <p className="mt-1 text-juv-deep-chestnut/70">
              Manage system settings and configurations
            </p>
          </div>

          <Tabs defaultValue="general" className="space-y-6">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="treatments">Treatments</TabsTrigger>
              <TabsTrigger value="locations">Locations</TabsTrigger>
              <TabsTrigger value="staff">Staff</TabsTrigger>
              <TabsTrigger value="notifications">Notifications</TabsTrigger>
              <TabsTrigger value="security">Security</TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="space-y-6">
              <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
                <CardHeader>
                  <CardTitle className="font-juv-heading text-juv-deep-chestnut flex items-center">
                    <Settings className="h-5 w-5 mr-2 text-juv-terracotta" />
                    General Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-juv-deep-chestnut mb-3">Business Hours</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Monday - Friday:</span>
                          <span>9:00 AM - 7:00 PM</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Saturday:</span>
                          <span>9:00 AM - 5:00 PM</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Sunday:</span>
                          <span>Closed</span>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" className="mt-3">
                        Edit Hours
                      </Button>
                    </div>
                    <div>
                      <h4 className="font-medium text-juv-deep-chestnut mb-3">Appointment Settings</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Default appointment length:</span>
                          <span>60 minutes</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Booking buffer:</span>
                          <span>15 minutes</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Advance booking limit:</span>
                          <span>90 days</span>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" className="mt-3">
                        Edit Settings
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="treatments" className="space-y-6">
              <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
                <CardHeader>
                  <CardTitle className="font-juv-heading text-juv-deep-chestnut">
                    Treatment Categories
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Button variant="outline" className="h-20 flex-col space-y-2">
                      <span className="font-medium">Injectables</span>
                      <span className="text-sm text-gray-600">8 treatments</span>
                    </Button>
                    <Button variant="outline" className="h-20 flex-col space-y-2">
                      <span className="font-medium">Facials</span>
                      <span className="text-sm text-gray-600">6 treatments</span>
                    </Button>
                    <Button variant="outline" className="h-20 flex-col space-y-2">
                      <span className="font-medium">Laser Treatments</span>
                      <span className="text-sm text-gray-600">4 treatments</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="locations" className="space-y-6">
              <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
                <CardHeader>
                  <CardTitle className="font-juv-heading text-juv-deep-chestnut flex items-center">
                    <MapPin className="h-5 w-5 mr-2 text-juv-terracotta" />
                    Location Management
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 rounded-lg border border-juv-rose-clay/20">
                      <div>
                        <h4 className="font-medium text-juv-deep-chestnut">Austin Downtown</h4>
                        <p className="text-sm text-gray-600">123 Main St, Austin, TX 78701</p>
                      </div>
                      <Button variant="outline" size="sm">
                        Edit
                      </Button>
                    </div>
                    <div className="flex items-center justify-between p-4 rounded-lg border border-juv-rose-clay/20">
                      <div>
                        <h4 className="font-medium text-juv-deep-chestnut">Austin North</h4>
                        <p className="text-sm text-gray-600">456 Oak Ave, Austin, TX 78756</p>
                      </div>
                      <Button variant="outline" size="sm">
                        Edit
                      </Button>
                    </div>
                  </div>
                  <Button className="w-full mt-4 bg-juv-terracotta text-white">
                    Add New Location
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="staff" className="space-y-6">
              <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
                <CardHeader>
                  <CardTitle className="font-juv-heading text-juv-deep-chestnut flex items-center">
                    <Users className="h-5 w-5 mr-2 text-juv-terracotta" />
                    Staff Roles & Permissions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 rounded-lg border border-juv-rose-clay/20">
                        <h4 className="font-medium text-juv-deep-chestnut mb-2">Administrator</h4>
                        <p className="text-sm text-gray-600 mb-3">Full system access</p>
                        <Button variant="outline" size="sm">
                          Manage Permissions
                        </Button>
                      </div>
                      <div className="p-4 rounded-lg border border-juv-rose-clay/20">
                        <h4 className="font-medium text-juv-deep-chestnut mb-2">Coordinator</h4>
                        <p className="text-sm text-gray-600 mb-3">Staff and patient management</p>
                        <Button variant="outline" size="sm">
                          Manage Permissions
                        </Button>
                      </div>
                      <div className="p-4 rounded-lg border border-juv-rose-clay/20">
                        <h4 className="font-medium text-juv-deep-chestnut mb-2">Provider</h4>
                        <p className="text-sm text-gray-600 mb-3">Treatment and appointment access</p>
                        <Button variant="outline" size="sm">
                          Manage Permissions
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="notifications" className="space-y-6">
              <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
                <CardHeader>
                  <CardTitle className="font-juv-heading text-juv-deep-chestnut flex items-center">
                    <Bell className="h-5 w-5 mr-2 text-juv-terracotta" />
                    Notification Settings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-medium text-juv-deep-chestnut mb-3">Email Notifications</h4>
                      <div className="space-y-2">
                        <label className="flex items-center space-x-2">
                          <input type="checkbox" defaultChecked className="rounded" />
                          <span className="text-sm">New appointment bookings</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input type="checkbox" defaultChecked className="rounded" />
                          <span className="text-sm">Appointment cancellations</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input type="checkbox" className="rounded" />
                          <span className="text-sm">Daily revenue reports</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium text-juv-deep-chestnut mb-3">SMS Notifications</h4>
                      <div className="space-y-2">
                        <label className="flex items-center space-x-2">
                          <input type="checkbox" defaultChecked className="rounded" />
                          <span className="text-sm">Appointment reminders</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input type="checkbox" className="rounded" />
                          <span className="text-sm">Emergency notifications</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="security" className="space-y-6">
              <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
                <CardHeader>
                  <CardTitle className="font-juv-heading text-juv-deep-chestnut flex items-center">
                    <Shield className="h-5 w-5 mr-2 text-juv-terracotta" />
                    Security Settings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-medium text-juv-deep-chestnut mb-3">Access Control</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Session timeout</span>
                          <span className="text-sm">4 hours</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Failed login attempts</span>
                          <span className="text-sm">5 attempts</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Two-factor authentication</span>
                          <span className="text-sm text-green-600">Enabled</span>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" className="mt-3">
                        Update Security Settings
                      </Button>
                    </div>
                    <div>
                      <h4 className="font-medium text-juv-deep-chestnut mb-3">Data Protection</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Data encryption</span>
                          <span className="text-sm text-green-600">AES-256</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Backup frequency</span>
                          <span className="text-sm">Daily</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Audit logging</span>
                          <span className="text-sm text-green-600">Enabled</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default StaffSettings;
